import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { enrollmentSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { searchSuffix } from '@/lib/enrollment-navigation';

interface EnrollmentForm {
  status: string;
  enrollmentTypeId: string;
  expiresAt: string;
}

interface EnrollmentType {
  id: string;
  name: string;
}

interface EnrollmentDetail {
  id: string;
  status?: string;
  enrollmentTypeId?: string | null;
  expiresAt?: string | null;
  enrolledAt?: string;
  user?: { id: string; name: string; email?: string };
  course?: { id: string; title?: string; name?: string };
}

const LABEL = 'text-sm font-medium text-dash-on-surface-variant';
const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Activa' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'suspended', label: 'Suspendida' },
  { value: 'completed', label: 'Completada' },
];

function toDateInput(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export function EnrollmentFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const suffix = searchSuffix(searchParams);
  const showUrl = `/inscripciones/${id}${suffix}`;

  const { data: enrollment, isLoading } = useQuery({
    queryKey: ['enrollments', id],
    queryFn: () => api<EnrollmentDetail>(`/enrollments/${id}`),
    enabled: Boolean(id),
  });

  const { data: types } = useQuery({
    queryKey: ['enrollments', 'types'],
    queryFn: () => api<EnrollmentType[]>('/enrollments/types'),
  });

  const { register, handleSubmit } = useForm<EnrollmentForm>({
    values: enrollment
      ? {
          status: enrollment.status ?? 'active',
          enrollmentTypeId: enrollment.enrollmentTypeId ?? '',
          expiresAt: toDateInput(enrollment.expiresAt),
        }
      : undefined,
    defaultValues: {
      status: 'active',
      enrollmentTypeId: '',
      expiresAt: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: EnrollmentForm) =>
      api(`/enrollments/${id}`, {
        method: 'PATCH',
        body: {
          status: values.status,
          enrollmentTypeId: values.enrollmentTypeId || null,
          expiresAt: values.expiresAt ? values.expiresAt : null,
        },
      }),
    onSuccess: async () => {
      toast(enrollmentSuccessToast('edit'));
      await queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      navigate(showUrl);
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo guardar la matrícula'));
    },
  });

  const courseTitle = enrollment?.course?.title ?? enrollment?.course?.name ?? '—';

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to={showUrl} />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Editar matrícula</h1>
          <p className="mt-1 text-dash-on-surface-variant">
            {enrollment?.user?.name ?? 'Estudiante'} · {courseTitle}
          </p>
        </div>
      </div>

      {isLoading || !enrollment ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <div className="w-full">
          <div className="lms-credentials-glass-panel rounded-xl border border-dash-outline-variant p-6 shadow-sm md:p-8">
            <dl className="mb-6 grid gap-3 rounded-lg border border-dash-outline-variant bg-secondary-50/60 p-4 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-dash-on-surface-variant">Estudiante</dt>
                <dd className="font-medium text-dash-on-surface">{enrollment.user?.name}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-dash-on-surface-variant">Curso</dt>
                <dd className="font-medium text-dash-on-surface">{courseTitle}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-dash-on-surface-variant">Inscrito el</dt>
                <dd>
                  {enrollment.enrolledAt ? new Date(enrollment.enrolledAt).toLocaleDateString('es') : '—'}
                </dd>
              </div>
            </dl>

            <form className="space-y-6" onSubmit={handleSubmit((values) => mutation.mutate(values))}>
              <div className="space-y-2">
                <label htmlFor="enrollment-status" className={LABEL}>Estado</label>
                <select id="enrollment-status" className={`${FIELD} cursor-pointer`} {...register('status')}>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label htmlFor="enrollment-type" className={LABEL}>Tipo de matrícula</label>
                <select id="enrollment-type" className={`${FIELD} cursor-pointer`} {...register('enrollmentTypeId')}>
                  <option value="">Sin tipo</option>
                  {(types ?? []).map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label htmlFor="enrollment-expires" className={LABEL}>Vigencia (vencimiento)</label>
                <input
                  id="enrollment-expires"
                  type="date"
                  className={FIELD}
                  {...register('expiresAt')}
                />
                <p className="text-xs text-dash-on-surface-variant">Dejar vacío para sin vencimiento.</p>
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="inline-flex items-center justify-center rounded-lg bg-dash-primary px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 disabled:opacity-60"
                >
                  {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
                </button>
                <Link
                  to={showUrl}
                  className="inline-flex items-center justify-center rounded-lg border border-dash-outline-variant px-6 py-2.5 text-sm font-medium text-dash-on-surface-variant transition-colors hover:bg-secondary-100"
                >
                  Cancelar
                </Link>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
